/**
 * Back in stock — two surfaces, one submit routine.
 *  <notify-me>        global dialog (snippets/notify-me.liquid), opened by any [data-notify-me]
 *                     trigger: product cards, the sticky bar, the PDP button when inline is off.
 *  <notify-me-inline> form under the buy button (snippets/notify-me-inline.liquid).
 * A trigger whose data-product-id matches an inline form on the page scrolls to that form
 * instead of opening the dialog (sticky bar on the PDP).
 * Native provider posts Shopify's customer form with fetch; tags
 * `back-in-stock, product:<handle>, variant:<id>` feed a Flow / Klaviyo segment.
 * Shopify's bot challenge → full-page post; the success state is restored after the redirect
 * (sessionStorage + ?customer_posted=true). Without JS the forms post normally.
 */
(() => {
  const PENDING_KEY = 'hurtliche:notify-pending';

  const readPending = () => {
    try {
      return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    } catch (e) {
      return null;
    }
  };
  const writePending = (data) => {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode: the fallback still posts, only the restored state is lost */
    }
  };
  const clearPending = () => {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {
      /* ignore */
    }
  };
  const postedBack = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
  };

  const setLoading = (button, state) => {
    if (!button) return;
    button.classList.toggle('loading', state);
    button.toggleAttribute('aria-disabled', state);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !state);
  };

  /** Posts a notify form. Resolves 'success' | 'error' | 'challenge'. */
  const postNotifyForm = async (form) => {
    const body = new FormData(form);
    body.delete('consent');
    const response = await fetch(form.action, {
      method: 'POST',
      body,
      headers: { Accept: 'text/html' },
      redirect: 'follow',
    });
    const finalUrl = new URL(response.url, window.location.href);
    if (finalUrl.pathname.includes('/challenge')) return 'challenge';
    const posted =
      finalUrl.searchParams.get('customer_posted') === 'true' ||
      finalUrl.searchParams.get('contact_posted') === 'true';
    return response.ok && posted ? 'success' : 'error';
  };

  /** Shared submit handler: `host` exposes showSuccess(), showError(), pendingData(). */
  const handleSubmit = async (event, host) => {
    if (!window.fetch) return;
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('[type="submit"]');
    host.reset();
    setLoading(button, true);
    let handoff = false;
    try {
      const result = await postNotifyForm(form);
      if (result !== 'success') {
        // Challenge or unreadable response. Hand the form to the native flow
        // instead of claiming a failure we cannot prove: a real navigation
        // clears the challenge and succeeds.
        handoff = true;
        writePending(host.pendingData());
        form.submit();
        return;
      }
      host.showSuccess();
    } catch (error) {
      host.showError();
    } finally {
      if (!handoff) setLoading(button, false);
    }
  };

  if (!customElements.get('notify-me-inline')) {
    customElements.define(
      'notify-me-inline',
      class NotifyMeInline extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('[data-notify-form]');
          this.fields = this.querySelector('[data-fields]');
          this.success = this.querySelector('[data-success]');
          this.error = this.querySelector('[data-error]');
          this.email = this.querySelector('input[type="email"]');
          this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
          this.form?.addEventListener('submit', (e) => handleSubmit(e, this));
          this.restoreAfterChallenge();
        }

        pendingData() {
          return { variantId: this.dataset.variantId };
        }

        restoreAfterChallenge() {
          const pending = readPending();
          if (!pending || !postedBack()) return;
          if (pending.variantId !== this.dataset.variantId) return;
          clearPending();
          this.showSuccess();
        }

        /** Called by the dialog when a trigger for this product is clicked (sticky bar). */
        focusField() {
          this.scrollIntoView({ block: 'center', behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
          const target = this.classList.contains('is-done') ? this.success : this.email;
          target?.focus({ preventScroll: true });
        }

        reset() {
          if (this.error) this.error.hidden = true;
          this.email?.removeAttribute('aria-invalid');
        }

        showSuccess() {
          this.classList.add('is-done');
          if (this.fields) this.fields.hidden = true;
          if (this.success) {
            this.success.hidden = false;
            this.success.focus();
          }
        }

        showError() {
          if (this.error) this.error.hidden = false;
          this.email?.setAttribute('aria-invalid', 'true');
        }
      }
    );
  }

  if (!customElements.get('notify-me')) {
    customElements.define(
      'notify-me',
      class NotifyMe extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.form = this.querySelector('[data-notify-form]');
          this.titleEl = this.querySelector('[data-product-title]');
          this.tagsInput = this.querySelector('[data-tags]');
          this.success = this.querySelector('[data-success]');
          this.error = this.querySelector('[data-error]');

          document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-notify-me]');
            if (!trigger) return;
            e.preventDefault();
            this.open(trigger);
          });

          this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
          this.dialog?.addEventListener('click', (e) => {
            if (e.target === this.dialog) this.close();
          });

          this.form?.addEventListener('submit', (e) => handleSubmit(e, this));
          this.restoreAfterChallenge();
        }

        inlineFormFor(productId) {
          if (!productId || !window.CSS?.escape) return null;
          return document.querySelector(`notify-me-inline[data-product-id="${CSS.escape(productId)}"]`);
        }

        open(trigger) {
          const { productTitle = '', productHandle = '', productId = '', variantId = '', variantTitle = '' } =
            trigger.dataset;

          // Same product has the inline form on this page: go there, no dialog.
          const inline = this.inlineFormFor(productId);
          if (inline && typeof inline.focusField === 'function') {
            inline.focusField();
            return;
          }

          this.trigger = trigger;
          this.pending = { productTitle, variantTitle, variantId };
          this.setTitle(productTitle, variantTitle);
          if (this.tagsInput) {
            this.tagsInput.value = ['back-in-stock', productHandle && `product:${productHandle}`, variantId && `variant:${variantId}`]
              .filter(Boolean)
              .join(', ');
          }
          this.dialog?.classList.remove('is-done');
          this.reset();
          if (this.success) this.success.hidden = true;
          this.show();
          this.dialog?.querySelector('input:not([type=hidden])')?.focus();
        }

        setTitle(productTitle, variantTitle) {
          if (this.titleEl) this.titleEl.textContent = variantTitle ? `${productTitle} · ${variantTitle}` : productTitle;
        }

        show() {
          if (typeof this.dialog?.showModal !== 'function') return;
          this.dialog.showModal();
          document.body.classList.add('overflow-hidden');
        }

        close() {
          this.dialog?.close();
          document.body.classList.remove('overflow-hidden');
          this.trigger?.focus();
        }

        pendingData() {
          return { ...this.pending, dialog: true };
        }

        restoreAfterChallenge() {
          const pending = readPending();
          if (!pending || !pending.dialog || !postedBack()) return;
          clearPending();
          this.setTitle(pending.productTitle || '', pending.variantTitle || '');
          this.showSuccess();
          this.show();
          this.success?.focus();
        }

        reset() {
          if (this.error) this.error.hidden = true;
        }

        showSuccess() {
          if (this.success) this.success.hidden = false;
          this.dialog?.classList.add('is-done');
          this.success?.focus?.();
        }

        showError() {
          if (this.error) this.error.hidden = false;
        }
      }
    );
  }
})();
